import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  AfterViewInit,
  ElementRef,
  ViewChild,
  OnChanges,
  ChangeDetectorRef,
  TemplateRef,
  ChangeDetectionStrategy,
  NgZone,
  afterNextRender,
  PLATFORM_ID,
  inject
} from '@angular/core';
import { Params, Router } from '@angular/router';
import { faCaretDown, faPlay, faPause, faVolumeMute, faVolumeUp, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Subscription, BehaviorSubject, first } from 'rxjs';

import { AssetType, Environment } from '@environment';
import { CompetitionsService } from '@usheru-hff/movie-detail/feature-competitions';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { CustomMovie, getMovieGenres, getMovieTags, Movie } from '@usheru-hff/shared/domain';
import { Video, VideoComponent, VideoFullscreenService } from '@usheru-hff/shared/ui-video';
import {
  CookiesCheck,
  CookieSettings,
  CookieSettingsService,
  CookiesSettingsType
} from '@usheru-hff/shared/utils-cookies';
import { EnvironmentService } from '@usheru-hff/shared/utils-environment';
import { ImageService } from '@usheru-hff/shared/utils-image';
import { TrackingService } from '@usheru-hff/sdk/tracking';
import { isPlatformBrowser } from '@angular/common';

/**
 * Component to actives a {@link VideoComponent} or {@link VideoFullscreenComponent}. Before that there is
 * a image in the video place with other elements and controls.
 *
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 *
 *
 *
 */
@Component({
  selector: 'ush-video-thumbnail',
  templateUrl: './video-thumbnail.component.html',
  styleUrls: ['./video-thumbnail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideoThumbnailComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges, CookiesCheck {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /**
   * @docNotNeccesary
   */

  private _movie: Movie;
  get movie() {
    return this._movie;
  }
  @Input() set movie(movie: Movie) {
    this._movie = movie;
  }

  /**
   * Used to put exceptions from branded pages
   */
  @Input() customMovie: CustomMovie;

  /**
   * Used in the {@link CarouselComponent} channel type to active some configs
   * for this setups.
   */
  @Input()
  isChannelVideo?: boolean;

  /**
   * Used in  {@link CarouselComponent} videos type and {@link GalleryComponent}.
   */
  @Input()
  video?: Video;

  /**
   * Used to reproduce video in {@link VideoFullscreenComponent}.
   */
  @Input()
  hasFullVideo?: boolean;

  /**
   * Priorice the {@link video} over {@link movie} trailer.
   */
  @Input()
  videoMovie = false;

  @Input()
  autoplay = false;

  /**
   * @docNotNeccesary
   */
  @Input()
  mute = false;

  /**
   * Reproduce the video in the same space and not {@link VideoFullscreenComponent}.
   */
  @Input()
  embed = false;

  @Input()
  loop = true;

  /**
   * heroVideoThumbnail is meant for video thumbnail that occupies 100vw tipically. It will change the "sizes" attribute of the main image for responsive reasons.
   */
  @Input()
  heroVideoThumbnail = false;

  /**
   * Hide native video controls of  {@link VideoComponent}.
   */
  @Input()
  embedControls: boolean;

  @Input()
  shareMovie = false;

  hasUplayer = false;

  @Input()
  customMovieLogo;

  uPlayerPurchased = false;

  triggerUplayerPlay: BehaviorSubject<boolean>;

  @Input() hasCompetition: boolean;

  /**
   * Activate the banner to go to the competition page.
   */
  hasCompetitionCta = false;
  competitionContent!: string;
  competitionLink!: string;
  competitionQParams!: Params;

  @Input()
  triggerPlay = new EventEmitter<void>();

  @Input()
  triggerClose: EventEmitter<void> = null;

  @Input()
  showArrows: boolean;

  @Output()
  arrowEvent = new EventEmitter<string>();

  @Output()
  videoReady = new EventEmitter();

  @Input()
  ignoreEnvParams = false;

  @Input()
  ignoreControls = false;

  @Input()
  forceRegularControls = false;

  @Input()
  forceFullScreeVideo = false;

  @ViewChild('controls')
  controlsElementRef: ElementRef;

  @Input()
  ignoreMovie = false;

  @Input()
  trackingCategory: string;

  @Input()
  current: boolean;

  @Input() seeMore = false;

  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('seeMore') seeMoreEvent = new EventEmitter();

  @Output()
  competitionRequested = new EventEmitter<void>();

  faPlay = faPlay;

  faPause = faPause;

  faMute = faVolumeMute;

  faVolumeUp = faVolumeUp;

  faCaretDown = faCaretDown;

  faTimes = faTimes;

  hideCompetitionBtn: boolean;

  @Input() seeMoreType!: number;

  @Input() seeMorePos!: 'top' | 'middle' | 'buttons' | 'none';

  channelCarouselImgClickable: boolean;

  videoExists = false;

  cookiesRequired: CookiesSettingsType[] = ['functionality_storage'];

  _active = false;
  get active() {
    return this._active;
  }
  set active(value: boolean) {
    this.videoIsReady.next(value);
    this._active = value;
    this.cd.detectChanges();
  }

  allowPlayback = true;

  placeholder: string;

  playingVideo = false;

  private cookieSettingsSubscription: Subscription;

  @ViewChild('videoThumbnail')
  videoThumbnailRef: ElementRef;

  @ViewChild(VideoComponent)
  videoComponent: VideoComponent;

  videoIsReady = new BehaviorSubject(false);

  environment: Environment;

  /**
   * Activates a button to scroll under the element
   */
  @Input() scrollLargeVideoButon = false;

  /**
   * Makes {@link scrollLargeVideoButon} really visible when the element height
   * is close to the window height (if the header height is ignored).
   */
  scrollLargeVideoButonVisible = false;

  /**
   * Status to know if the video fill the width space. Setted in {@link onResize}.
   */
  videoFullWidth = false;

  /**
   * Status to know if the video is in the top. Setted in {@link onResize}.
   */
  videoAtTop = false;

  /**
   * Status to activate a css class and fit the controls to the screen.
   */
  controlsVisibleFlatAspectRatio = false;

  @Input()
  extraControlsContent: TemplateRef<unknown>[] = [];

  onResizeInicialited = false;

  landscapePlaceholder: string;

  @Input()
  iconPlay?: AssetType;

  @ViewChild('iconPlayTemplate') iconPlayRef: TemplateRef<unknown>;

  $enviObservable!: Subscription;
  $cookieObservable!: Subscription;

  constructor(
    private videoService: VideoFullscreenService,
    private envservice: EnvironmentService,
    private cookieSettingsService: CookieSettingsService,
    private channelService: ChannelService,
    private router: Router,
    private competitionService: CompetitionsService,
    private trackingService: TrackingService,
    private cd: ChangeDetectorRef,
    private zone: NgZone,
    private imageService: ImageService
  ) {
    this.environment = this.envservice.environment$.value;
    this.landscapePlaceholder = this.imageService.landscapePlaceholder;
    afterNextRender(() => {
      // this.calculateSize();
      if (this.hasCompetition) this.setCompetitionsCta(this.hasCompetition);
    });
  }

  ngOnChanges(/*changes: SimpleChanges*/): void {
    if (this.current && this.autoplay && this.allowPlayback) {
      this.play();
    }

    this.initListeners();
  }

  onResize() {
    this.zone.runOutsideAngular(() => {
      addEventListener('resize', event => {
        // why do we need to calculate size? we should not calculate any size here
        // this.calculateSize();
      });
    });
  }

  get isScullyRunning() {
    return false;
  }

  ngOnInit() {
    const cookieSettings = this.cookieSettingsService.getCookieSettings();
    this.handleCookieSettings(cookieSettings);
    // if (false) {
    this.allowPlayback = true;
    // }

    if (this.autoplay && this.allowPlayback && !this.embed) {
      this.videoService.play({
        content: this.movie ? this.movie : this.video,
        category: this.trackingCategory,
        animate: false
      });
    }

    this.hasUplayer = this.movie?.hasPlayer;

    // //if embed and autoplay make the active true
    if (this.embed && this.allowPlayback && this.autoplay) {
      this.active = true;
      this.playingVideo = true;
      // if it is a movie the check if trailer exists first if not do not try to make it play.
      if (this.movie && !this.movie?.trailerLink) {
        this.active = false;
        this.playingVideo = false;
      }
    }

    const videoUrl = this.video
      ? this.video.video || this.video.trailer
      : this.movie.trTrailerLink || this.movie.fullVideoUrl;

    this.videoExists = videoUrl ? true : false;

    // listen environment changes
    this.$enviObservable = this.envservice.environment$.subscribe(env => {
      this.environment = env;
      this.loadEnvironmentVariables();
    });
  }

  ngAfterViewInit() {
    this.initListeners();
    this.handleVideoReady();
    // todo: refactor this, set interval is not the best way to do this
    const itervalCheckVideoSize = setInterval(() => {
      const videoTh = this.videoThumbnailRef.nativeElement as HTMLElement;
      if (videoTh.clientHeight > 0) {
        this.onResize();
        clearInterval(itervalCheckVideoSize);
        return;
      }
      if (!videoTh) clearInterval(itervalCheckVideoSize);
    }, 250);
    this.cd.detectChanges();
  }

  ngOnDestroy() {
    if (this.$enviObservable) this.$enviObservable.unsubscribe();
    if (this.$cookieObservable) this.$cookieObservable?.unsubscribe();
  }

  private handleCookieSettings(cookieSettings: CookieSettings): void {
    this.allowPlayback = cookieSettings?.functionality_storage?.enabled;
  }

  playVideo() {
    if (this.uPlayerPurchased) {
      this.playUPlayer();
    } else {
      this.play();
    }
  }

  playTrailer() {
    this.play();
  }

  play() {
    if (this.playingVideo) return;
    if (this.embed) {
      this.active = true;
      this.playingVideo = true;

      if (this.videoIsReady.value) {
        this.videoComponent.reanude();
      } else {
        this.videoIsReady.pipe(first(val => val)).subscribe(() => {
          this.videoComponent.reanude();
        });
      }
      // Check if video started; if not force the play again but muted
      // if the user didn't interactue with anything in the app the video with audio
      // can not start
      this.videoIsReady.pipe(first(val => val)).subscribe(() => {
        this.videoComponent.reanude();
      });
    } else {
      // if this videoMovie means its a custom video with trailer and video so play trailer
      if (this.videoMovie) {
        this.videoService.play({
          content: { url: this.video?.video || this.video?.trailer, title: this.video.title },
          category: this.trackingCategory
        });
      } else {
        this.videoService.play({ content: this.video ? this.video : this.movie, category: this.trackingCategory });
      }
    }
  }

  pause(args?: { reset?: boolean; ignoreComponent?: boolean }) {
    const { reset = false, ignoreComponent = false } = args || {};
    if (this.embed) {
      if (!reset) {
        this.playingVideo = false;
        this.videoComponent?.pause();
      } else {
        this.active = false;
        this.playingVideo = false;
        if (this.videoComponent && !ignoreComponent) this.videoComponent.pause();
      }
    } else {
      this.videoService.setEvent({ event: 'pause' });
    }
  }

  // play pause buttons for embeded video
  toggleMute(value?: boolean) {
    this.mute = value !== undefined ? value : !this.mute;
    if (this.mute) {
      this.videoComponent.setMute();
    } else {
      this.videoComponent.setUnmute();
    }
  }

  togglePlay() {
    if (this.playingVideo) {
      this.pause();
    } else {
      this.play();
    }
    this.playingVideo = !this.playingVideo;
  }

  playFullVideo() {
    if (this.embed) {
      this.active = true;
    } else {
      this.videoService.play({
        content: {
          url: this.video?.video || this.movie?.fullVideoUrl,
          title: this.video?.title || this.movie?.title
        }
      });
    }
  }

  playUPlayer() {
    if (this.triggerUplayerPlay) {
      this.triggerUplayerPlay.next(true);
    }
  }

  get isExternalLink() {
    return this.video?.ctaUrl?.indexOf('http') == 0;
  }

  getCtaRouterLink() {
    let url = null;
    if (this.video?.ctaUrl) {
      url = this.video.ctaUrl;
    } else {
      const movieFriendly = this.video ? this.video.movieFriendlyUrl : this.movie.friendlyUrl;

      if (!movieFriendly) {
        url = null;
      } else {
        url = `/movie/${movieFriendly}`;
      }
    }
    return url?.split('?')[0];
  }

  getCtaLinkParams(url) {
    if (!url) return;
    const queryString = url.split('?')[1];
    if (!queryString) return {};

    const queryParams = queryString.split('&');
    const paramsObject = {};

    queryParams.forEach(param => {
      const [key, value] = param.split('=');
      paramsObject[key] = decodeURIComponent(value);
    });
    return paramsObject;
  }

  getCtaLinkState() {
    const movie = this.video ? this.video.movie : this.movie;
    return { movie: movie };
  }

  handleVideoReady() {
    this.videoReady.emit(true);
  }

  handleSeeMore() {
    this.seeMoreEvent.emit(true);
  }

  handleCtaClick() {
    // only runs if hasFullVideo
    if (this.hasFullVideo || this.movie?.fullVideoUrl) {
      this.playFullVideo();
      return;
    }
    // if movie exists then track the SelectMovie event
    if (this.isChannelVideo && this.video.movie) this.trackChannelMovieCtaClicked(this.video.movie);
  }

  isThereLogo(): boolean {
    if (this.customMovieLogo === '') return false;
    if (this.customMovieLogo) return true;
    if (this.customMovie?.showMovieLogo && this.movie?.logoUrl) return true;
    if (this.environment.customPageParams.movieDetailNoMovieLogo) return false;
    if (this.videoMovie) {
      return false;
    }
    if (this.video) {
      if (this.video.movieId) {
        return typeof this.video.movieLogoUrl !== 'undefined' && this.video.movieLogoUrl !== null;
      } else return false;
    }
    if (this.movie) {
      return typeof this.movie.logoUrl !== 'undefined';
    }
    return false;
  }

  initListeners() {
    this.hasUplayer = this.movie?.hasPlayer;
    if (this.embed == true && this.triggerPlay !== null) {
      this.triggerPlay.subscribe(() => {
        this.triggerPlayVideo();
      });
    }

    if (this.embed == true && this.triggerClose != null) {
      this.triggerClose.subscribe((val: boolean) => {
        if (val) this.triggerCloseVideo();
      });
    }

    if (!this.allowPlayback) {
      this.$cookieObservable = this.cookieSettingsService.$cookieSettingsChanged.subscribe(settings => {
        this.handleCookieSettings(settings);
        this.ngOnInit();
      });
    }
  }

  triggerCloseVideo() {
    this.active = false;
  }

  triggerPlayVideo() {
    if (this.embed) {
      this.play();
    }
  }

  private loadEnvironmentVariables() {
    if (!this.ignoreEnvParams) {
      // this is only be used by a carousel slider
      if (this.environment.channelCarouselImgRedirectOnClick) {
        this.channelCarouselImgClickable = this.environment.channelCarouselImgRedirectOnClick;
      }

      if (!this.seeMoreType) this.seeMoreType = this.environment?.customPageParams?.seeMoreType || 1;
      if (!this.seeMorePos) this.seeMorePos = this.environment?.customPageParams?.seeMorePos || 'top';
    }
  }

  prev() {
    this.arrowEvent.emit('prev');
  }

  next() {
    this.arrowEvent.emit('next');
  }

  setCompetitionsCta(hasCompetition: boolean) {
    this.hasCompetitionCta =
      hasCompetition && this.movie && !this.competitionService.isCompetitionSubmitted(this.movie.id);

    const defaultLink = `/competitions/${this.movie.id}`;

    if (this.hasCompetitionCta) {
      const customCompetitions = this.environment?.competitionsCustom || {};
      const customCompetition = customCompetitions[this.movie.friendlyUrl];

      if (customCompetition) {
        this.competitionContent = customCompetition.bannerContent;
        this.competitionLink = customCompetition.url || defaultLink;
        this.competitionQParams = customCompetition.qParams || {};
      } else {
        this.competitionLink = defaultLink;
        this.competitionQParams = {};
      }
    }
  }

  endVideo() {
    if (!this.autoplay) {
      this.pause();
      this.playingVideo = false;
    }
  }

  onMovieVideoPicClicked(video: Video) {
    this.router.navigate(['/movie/' + video.movie?.friendlyUrl]);
  }

  competitionSelected(): void {
    this.competitionService.getCompetition(this.movie.id).subscribe(competition => {
      this.trackingService.trackEvent({
        action: 'SelectCompetition',
        properties: {
          label: `${competition.title} @ ${this.movie.title}`,
          category: 'Competitions',
          movie: this.movie.title,
          idMovie: this.movie.id,
          imdb: this.movie?.imdb,
          tagsMovie: getMovieTags(this.movie),
          genresMovie: getMovieGenres(this.movie),
          competitionTitle: competition.title,
          idCompetition: competition.id
        }
      });
    });
  }

  trackChannelMovieCtaClicked(movie: Movie) {
    this.trackingService.trackEvent({
      action: 'SelectMovie',
      properties: {
        label: `${movie.title}`,
        category: 'ChannelGallery',
        movie: movie.title,
        idMovie: movie.id,
        imdb: movie.imdb,
        tagsMovie: getMovieTags(movie),
        genresMovie: getMovieGenres(movie)
      }
    });
  }

  handleUplayPurchased(e: boolean) {
    this.uPlayerPurchased = e;

    if (this.uPlayerPurchased && !this.triggerUplayerPlay) {
      this.triggerUplayerPlay = new BehaviorSubject(false);
    }
  }

  onMoviePicClicked(elem) {
    if (elem?.movie) {
      this.router.navigate(['/movie/' + elem.movie.friendlyUrl]);
    }
  }

  onChannelCarouselImg() {
    if (!this.channelCarouselImgClickable) {
      return;
    }
    if (this.video?.movieId) {
      this.router.navigate(['/movie/' + this.video.movieFriendlyUrl]);
    }
  }

  scrollLargeVideo() {
    const el = this.videoThumbnailRef.nativeElement;
    const contentWrapper = document.querySelector('.content-wrapper');
    const offsetMargin = parseInt(window.getComputedStyle(contentWrapper).marginTop, 10);
    const offesetHeader = document.querySelector('.header-wrapper')?.clientHeight || 0;
    const offsetY = offesetHeader + offsetMargin;
    const elelemntTop = el.getBoundingClientRect().top + window.pageYOffset + el.clientHeight - offsetY;
    window.scrollTo({ top: elelemntTop, behavior: 'smooth' });
  }
}
